/* eslint-disable max-depth */
/* eslint-disable complexity */
/* eslint-disable max-statements */
import { getPlatformId } from '@ds/swymkit/stores'
import { cacheDecorator } from '@ds/swymkit/tools'

import { aiAssistantInfraFetch } from '~/services/fetch-ai-assistant-infra'
import { llmInferenceFetch } from '~/services/fetch-llminference'
import { swymFetch } from '~/services/fetch-swym'

import type { IMedia } from '~/entities/content/media/interfaces/media'

import { parseAIAssistants } from '~/features-swym/aura/api/parser-ai-response'
import { AI_ASSISTANT_TIMEOUT, AI_MODEL } from '~/features-swym/aura/config/ai'
import type { AskAIAIAgentOptions, AskAIAssistantOptions } from '~/features-swym/aura/interfaces/ai'
import type { AIAssistantName } from '~/features-swym/aura/interfaces/ai-assistants'

import type { SUBJECT_6W_URI } from '~/features-shared/libs/interfaces/entity'
import { sha256 } from '~/features-shared/libs/utils/sha256'

import { useFeatureManager } from '~/features-generic/config-manager/composables/feature-manager'
import { APPNAME } from '~/features-generic/libs/config/widget-app-name'
import { getUWPAppProperty } from '~/features-generic/uwp/api/uwp'

/**
 * Ask something (input) to the AI service.
 * The input will not be modified.
 *
 * @param input input to send to LLMInference service. It will not be modified
 * @param parse set true to parse content as Markdown, default is true
 * @returns AI response Markdown parsed so it SHOULD be HTML
 */
export async function askRawLlmI(input: string, parse = true): Promise<string> {
  const model = (await getUWPAppProperty('app.swymfeature.copilot.llmi.model')) || AI_MODEL
  return llmInferenceFetch.post('/v1/chat/completions', {
    json: {
      model,
      messages: [{ role: 'user', content: input }],
      max_tokens: Number.parseInt((3000 - (input.split(' ').length * 2)).toFixed(0), 10),
    },
  })
    .json<{ choices?: { message?: { content?: string } }[] }>()
    .then(async (response): Promise<string> => {
      const result = response?.choices?.[0]?.message?.content || ''
      if (!parse)
        return result

      const { default: MarkdownIt } = await import('markdown-it')
      const md = new MarkdownIt()
      return md.render(result)
    })
}

/**
 * Ask something to an AIAssistantInfra agent.
 *
 * @param options Options to ask an AIAssistantInfra agent.
 * @param options.agent_name Name of the agent.
 * @param options.agent_version Version of the agent.
 * @param options.prompt User's prompt.
 * @param options.source_language Optional - Source language of the user's prompt.
 * @param options.target_language Optional - Target language in which the text will be translated.
 * @param parse set true to parse content as Markdown, default is true
 * @returns AI response Markdown parsed so it SHOULD be HTML
 */
export async function askAIAIAgent(options: AskAIAIAgentOptions, parse = true): Promise<string> {
  return aiAssistantInfraFetch.post('/api/v1/assistants/askaura/submit', {
    json: {
      'agent_name': options.agent_name,
      'agent_version': options.agent_version,
      'prompt': options.prompt,
      'source_language': options.source_language,
      'target_language': options.target_language,
      'llm.stream': false,
    },
    timeout: AI_ASSISTANT_TIMEOUT,
  })
    .json<{ llm?: { choices?: { message?: { content?: string } }[] } }>()
    .then(async (response): Promise<string> => {
      const result = response?.llm?.choices?.[0]?.message?.content || ''
      if (!parse)
        return result

      const { default: MarkdownIt } = await import('markdown-it')
      const md = new MarkdownIt()
      return md.render(result)
    })
}

/**
 * Concatenate two arrays of 8-bit unsigned integer values.
 * @param a First array.
 * @param b Second array.
 * @returns A concatenated array.
 */
function concatUint8Array(a: Uint8Array, b: Uint8Array): Uint8Array {
  const result = new Uint8Array(a.length + b.length)
  result.set(a)
  result.set(b, a.length)
  return result
}

/**
 * Ask something (input) to the AIAssistantInfra service.
 * @param options Options to ask the assistant.
 * @param options.input User's input message.
 * @param options.conversation_id Id of the conversation.
 * @param options.temp_message_id Temporary ID associated to the current AI answer.
 * @param options.callbacks Callbacks used for the sse events.
 * @param options.signal An abort signal to stop the answer.
 * @param options.firstMessage Is first message of the conversation.
 */
export async function askAIAssistant(
  options: AskAIAssistantOptions,
): Promise<void> {
  const isRephrasingQueriesActivated = useFeatureManager('ai.aura.rephrasing_queries')

  return aiAssistantInfraFetch.post('/api/v1/assistants/aura/submit', {
    method: 'POST',
    timeout: AI_ASSISTANT_TIMEOUT,
    json: {
      'conversation_id': options.conversation_uri,
      'prompt': options.input,
      'llm.stream': true,
      'chain_params.title_generation': options.firstMessage,
      /** This option is used to enable rephrasing queries from the llm response should be true to have them  */
      'human_feedback.enabled': isRephrasingQueriesActivated.value,
    },
    signal: options.signal,
  })
    .then(async (response) => {
      await options.callbacks?.onStreamOpenCallback(response)
      return response
    })
    .then(async (response) => {
      // eslint-disable-next-line no-async-promise-executor
      return new Promise(async (resolve, reject): Promise<void> => {
        try {
          /** Temporarily holds the incoming bytes of data */
          let buffer: Uint8Array | undefined
          /** Keeps track of the current read position in the buffer */
          let position: number = 0
          /** Stores the length of the "field" part of a line (the part before the first colon :) */
          let fieldLength: number = 0

          const reader = response.body!.getReader()
          let { value, done } = await reader?.read()
          let chunkCount = 0
          while (value && !done) {
            if (buffer === undefined) {
              buffer = value
              position = 0
              fieldLength = -1
            }
            else {
              // Previous chunk has been processed but the line was not fully completed
              // Concatenate the new chunk to the buffer
              buffer = concatUint8Array(buffer, value)
            }

            const bufferLength = buffer.length
            let lineStart = 0
            while (position < bufferLength) {
              let lineEnd = -1
              for (; position < bufferLength && lineEnd === -1; ++position) {
                switch (buffer[position]) {
                  case 10: // If it's a new line \n char
                    // The lineEnd takes the current read position
                    lineEnd = position
                    break
                  case 58: // If it's a colon : char
                    // First colon in line, the one after data
                    if (fieldLength === -1)
                      fieldLength = position - lineStart
                    break
                }
              }

              if (lineEnd === -1) {
                // End of the buffer but line is not complete. Continue parsing.
                break
              }

              const decoder = new TextDecoder()
              const line = buffer.subarray(lineStart, lineEnd)
              if (line.length !== 0 && fieldLength > 0) {
                /** Field name, which is "data" for now, that stands at the start of each chunk. */
                const field = decoder.decode(line.subarray(0, fieldLength))
                /**
                 * Offset that corresponds to the "data:" we do not need.
                 * - If there is a space after the "data:", we increase the offset by 1.
                 */
                const valueOffset = fieldLength + (line[fieldLength + 1] === 32 ? 2 : 1)
                /** Value of each chunk. */
                const value = decoder.decode(line.subarray(valueOffset))

                let answerTime: number = 0
                let firstChunk: boolean = false

                switch (field) {
                  case 'data':
                    chunkCount++

                    if (chunkCount === 3) {
                      // First chunk containing content. The one after the sources' chunk.
                      // Useful for the Analytics about the elapsed time between the start and the first chunk.
                      firstChunk = true
                      answerTime = Date.now() - options.startedAt
                    }

                    // The response can be valid but with a body that says Internal Server Error
                    if (value === 'Internal Server Error')
                      throw new Error(value)

                    await options.callbacks?.onStreamMessageCallback({
                      data: JSON.parse(value),
                      conversation_uri: options.conversation_uri,
                      temp_message_id: options.temp_message_id,
                      timeElapsed: answerTime,
                      firstChunk,
                    })
                    break
                }
              }

              lineStart = position
              fieldLength = -1
            }

            if (lineStart === bufferLength) {
              // We've finished reading the buffer
              buffer = undefined
            }
            else if (lineStart !== 0) {
              // If the buffer was not completely processed, we keep the unprocessed part by creating a new view of the buffer starting from lineStart
              buffer = buffer.subarray(lineStart)
              position -= lineStart
            }

            ({ value, done } = await reader?.read())
          }
        }
        catch (error) {
          if (error === 'user-stop')
            reject(error)
        }

        resolve(response)
      })
    })
    .then(() => {
      return options.callbacks?.onStreamCloseCallback({
        conversation_uri: options.conversation_uri,
        temp_message_id: options.temp_message_id,
        input: options.input,
        responseDuration: Date.now() - options.startedAt,
        firstMessage: options.firstMessage,
      })
    })
}

/**
 * Get the available AI Assistants.
 *
 * Uses the AIAI api that returns info on the assistants.
 *
 * @returns A list of names of available assistants.
 */
export async function getAIAssistants(): Promise<AIAssistantName[]> {
  return await aiAssistantInfraFetch.get('/api/v1/assistants/')
    .json<{ assistant__name: AIAssistantName }[]>()
    .then((response): AIAssistantName[] => {
      return parseAIAssistants(response)
    })
}

/**
 * Force media to be ingested.
 *
 * @param media a media list to be ingested.
 * @param conversationUri The conversation URI that contains the media
 * @param userLogin The connected user login
 */
export async function ingestMedia(media: IMedia[], conversationUri: SUBJECT_6W_URI, userLogin: string) {
  const userHash = await sha256(userLogin)
  return await aiAssistantInfraFetch.post('/api/v1/datasources/conversationmemory/ingest', {
    searchParams: {
      datasourceVersion: '2.0.0',
    },
    json: await Promise.all(media.map(async media => ({
      x3dcontent__service__id: '3DSwym',
      x3dcontent__content__type: 'MEDIA',
      media__uri: media.play.source.physicalid,
      event__tenant: getPlatformId(),
      r_timestamp: Date.now(),
      event__action: 'CREATE',
      event__id: media.subject_uri,
      event__start: Date.now(),
      swym__content__owner__hash: await sha256(media.author.login),
      user__hash: userHash,
      event__modified: Date.now(),
      conversation__id: conversationUri,
      r_schemaName: 'media',
      x3dcontent__app__id: APPNAME,
      event__to_delete: false,
      swym__content__uri: media.subject_uri,
      content__title: media.title,
      r_service: '3DSWYM', // AURA ?
      tenant__id: getPlatformId(),
      x3dcontent__content__id: media.subject_uri,
      media__filename: media.original_name,
      media__extension: media.media_file_ext,
    }))),
  })
    .json<{
    valid_count: number
    invalid_count: number
    unauthorized_count: number
  }>()
}

/**
 * For a given conversation uri, get the list of media that are still being ingested (means AURA can't answer question about it)
 *
 * @param conversationUri the uri of the conversation to check
 */
export async function getMediaConsumeStatus(conversationUri: SUBJECT_6W_URI) {
  return cacheDecorator(`media-ingest-for-conversation-${conversationUri}`, () => {
    return aiAssistantInfraFetch.post('/api/v1/datasources/conversationmemory/consume/media/_search', {
      json: {
        query: {
          bool: {
            filter: [
              { match_phrase: { 'conversation.id': conversationUri } },
            ],

            must_not: [
              { match_phrase: { 'tools.processed_by': 'doc_parser_1.0.0' } },
            ],
          },
        },
      }
      ,
    })
      .json<{
      took: number
      timed_out: boolean
      _shards: unknown
      hits: {
        hits: unknown[]
      }
    }>()
  }, 5000)
}
